import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Container, Row, Col } from "reactstrap"
import { Link } from "react-router-dom"
import FeatherIcon from "feather-icons-react"
import { TypeAnimation } from "react-type-animation"
import { SERVICE_KEYS, SERVICE_CONFIG } from "../../../constants"

const HeroSection = () => {
    const { t, i18n } = useTranslation()
    const [currentIndex, setCurrentIndex] = useState(0)

    // Rotate through services for icon
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % SERVICE_KEYS.length)
        }, 3500) // Match with typewriter timing
        return () => clearInterval(interval)
    }, [])

    const currentService = SERVICE_CONFIG[SERVICE_KEYS[currentIndex]]

    // Get service titles for typewriter effect
    const getTypewriterSequence = () => {
        const sequence = []
        SERVICE_KEYS.forEach((key, idx) => {
            sequence.push(t(`services.${key}.title`))
            sequence.push(2500) // Wait 2.5 seconds
        })
        return sequence
    }

    return (
        <section
            className="bg-half-170 d-table w-100"
            style={{
                background: "linear-gradient(135deg, #1E3A5F 0%, #0D1F33 100%)"
            }}
        >
            <Container>
                <Row className="align-items-center">
                    <Col lg={7} md={7}>
                        <div className="title-heading">
                            <h1 className="heading mb-3 text-white">
                                {t("hero.title")}
                            </h1>
                            <p className="para-desc text-white-50">
                                {t("hero.subtitle")}
                            </p>
                            <div className="mt-4 pt-2">
                                <Link to="/contact-us" className="btn btn-primary me-2">
                                    {t("hero.cta")}
                                    <FeatherIcon icon="arrow-right" className="fea icon-sm ms-1" />
                                </Link>
                                <a href="#services" className="btn btn-light">
                                    {t("hero.cta_secondary")}
                                </a>
                            </div>
                        </div>
                    </Col>
                    <Col lg={5} md={5} className="mt-4 pt-2 mt-sm-0 pt-sm-0">
                        {/* Premium Typewriter Box */}
                        <div
                            className="position-relative"
                            style={{
                                background: "linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
                                backdropFilter: "blur(20px)",
                                borderRadius: "16px",
                                padding: "32px",
                                border: "1px solid rgba(255,255,255,0.1)",
                                boxShadow: "0 20px 40px rgba(0,0,0,0.3)"
                            }}
                        >
                            {/* Animated Glow */}
                            <div
                                style={{
                                    position: "absolute",
                                    top: "-2px",
                                    left: "-2px",
                                    right: "-2px",
                                    bottom: "-2px",
                                    background: `linear-gradient(45deg, ${currentService.color}, transparent, ${currentService.color})`,
                                    borderRadius: "18px",
                                    zIndex: -1,
                                    opacity: 0.5,
                                    animation: "pulse 2s ease-in-out infinite"
                                }}
                            />

                            {/* Icon Badge */}
                            <div className="text-center mb-4">
                                <div
                                    className="d-inline-flex align-items-center justify-content-center"
                                    style={{
                                        width: "70px",
                                        height: "70px",
                                        borderRadius: "20px",
                                        background: `linear-gradient(135deg, ${currentService.color} 0%, ${currentService.color}CC 100%)`,
                                        boxShadow: `0 10px 30px ${currentService.color}40`,
                                        transition: "all 0.5s ease"
                                    }}
                                >
                                    <FeatherIcon
                                        icon={currentService.icon}
                                        style={{ width: "32px", height: "32px", color: "#fff" }}
                                    />
                                </div>
                            </div>

                            {/* Typewriter Text */}
                            <div
                                className="text-center"
                                style={{ minHeight: "60px" }}
                            >
                                <h4 className="mb-0" style={{ fontWeight: "600", lineHeight: "1.4" }}>
                                    <TypeAnimation
                                        key={i18n.language}
                                        sequence={getTypewriterSequence()}
                                        wrapper="span"
                                        speed={50}
                                        deletionSpeed={30}
                                        repeat={Infinity}
                                        cursor={true}
                                        style={{
                                            display: "inline-block",
                                            color: "#fff"
                                        }}
                                    />
                                </h4>
                            </div>

                            {/* Bottom Features */}
                            <div
                                className="d-flex justify-content-between mt-4 pt-3"
                                style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}
                            >
                                <div className="text-center flex-fill">
                                    <FeatherIcon icon="layers" style={{ width: "18px", height: "18px", color: "#E65100" }} />
                                    <small className="text-white-50 d-block mt-1" style={{ fontSize: "10px" }}>
                                        {i18n.language === 'tr' ? '9 Modül' : '9 Modules'}
                                    </small>
                                </div>
                                <div className="text-center flex-fill" style={{ borderLeft: "1px solid rgba(255,255,255,0.1)", borderRight: "1px solid rgba(255,255,255,0.1)" }}>
                                    <FeatherIcon icon="shield" style={{ width: "18px", height: "18px", color: "#E65100" }} />
                                    <small className="text-white-50 d-block mt-1" style={{ fontSize: "10px" }}>
                                        {i18n.language === 'tr' ? 'KVKK Uyumlu' : 'GDPR Ready'}
                                    </small>
                                </div>
                                <div className="text-center flex-fill">
                                    <FeatherIcon icon="cloud" style={{ width: "18px", height: "18px", color: "#E65100" }} />
                                    <small className="text-white-50 d-block mt-1" style={{ fontSize: "10px" }}>
                                        {i18n.language === 'tr' ? 'Bulut/Yerel' : 'Cloud/On-Prem'}
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* CSS Animation */}
                        <style>
                            {`
                                @keyframes pulse {
                                    0%, 100% { opacity: 0.3; }
                                    50% { opacity: 0.6; }
                                }
                            `}
                        </style>
                    </Col>
                </Row>
            </Container>
        </section>
    )
}

export default HeroSection
